<template>
	<div :class="`form-pdf-embed${interactive ? '' : ' links-disabled'}`">
		<div ref="pages" class="form-pdf-pages" role="document" :aria-label="documentLabel"></div>
		<div v-if="state === 'loading'" class="form-pdf-loading"><v-progress-circular indeterminate size="24" width="2"></v-progress-circular></div>
		<p v-else-if="state === 'error'" class="form-pdf-error">
			This PDF couldn't be displayed.
			<a :href="url" target="_blank" rel="noopener noreferrer">Open PDF</a>
		</p>
		<a v-if="state !== 'error'" class="form-pdf-sr-only" :href="url" target="_blank" rel="noopener noreferrer">Open {{ documentLabel }}</a>
	</div>
</template>

<script>
	// Chrome-free PDF display (see ./pdfRenderer.js) shared by form and agreement
	// PdfBlocks. interactive: false hides the link layer (builder selection).
	import { mountPdfEmbed } from '@/modules/forms/v2/schema/pdfRenderer';

	export default {
		name: 'PdfEmbed',

		props: {
			url: { type: String, required: true },
			fileName: { type: String, default: null },
			interactive: { type: Boolean, default: true },
		},

		data: function () {
			return { state: 'loading', embed: null };
		},

		mounted() {
			this.mountEmbed();
		},

		beforeDestroy() {
			if (this.embed) this.embed.destroy();
		},

		watch: {
			url: function () {
				this.mountEmbed();
			},
		},

		methods: {
			mountEmbed: function () {
				if (this.embed) this.embed.destroy();
				this.embed = mountPdfEmbed(this.$refs.pages, this.url, { onState: (state) => (this.state = state) });
			},
		},

		computed: {
			documentLabel: function () {
				return this.fileName ? `${this.fileName} (PDF)` : 'PDF document';
			},
		},
	};
</script>

<style scoped lang="scss">
	.form-pdf-embed {
		position: relative;

		&.links-disabled ::v-deep .annotationLayer { display: none; }
	}

	.form-pdf-pages {
		display: flex;
		flex-direction: column;
		gap: 12px;

		::v-deep .pdf-embed-page {
			position: relative;
			width: 100%;
			overflow: hidden;
			border-radius: 4px;
			background: #fff;
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
		}
		::v-deep .pdf-embed-canvas {
			position: absolute;
			top: 0;
			left: 0;
			display: block;
			width: 100%;
			height: 100%;
		}
		::v-deep .annotationLayer {
			position: absolute;
			top: 0;
			left: 0;
			pointer-events: none;
		}
		::v-deep .annotationLayer section {
			position: absolute;
			box-sizing: border-box;
			pointer-events: auto;
		}
		::v-deep .annotationLayer .linkAnnotation > a {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			font-size: 1em;
		}
	}

	.form-pdf-loading {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 160px;
	}

	.form-pdf-error {
		margin: 0;
		padding: 24px;
		border: 1px dashed rgba(0, 0, 0, 0.2);
		border-radius: 10px;
		text-align: center;
	}

	.form-pdf-sr-only {
		position: absolute;
		width: 1px;
		height: 1px;
		overflow: hidden;
		clip: rect(0 0 0 0);
		white-space: nowrap;
	}
</style>
